import React from 'react';

import { useCloningProgress } from '@/hooks/poc/useCloningProgress';
import { useFileCloneError, useFileCloneStatus } from '@/hooks/poc/useFileCloneStatus';
import type { FileEntry } from '@/types/SixDetail.types';
import type { FileCloneStatus } from '@/types/poc/helia';
import { formatBytes } from '@/utils/format.utils';

function getStatusLabel(status: FileCloneStatus, progress?: { bytesLoaded: number; totalBytes: number }, error?: string): string {
  if (status === 'cloning') {
    if (progress === undefined) return 'Transferring to browser';

    const loaded = formatBytes(progress.bytesLoaded);
    if (progress.totalBytes > 0) {
      return `Transferring: ${loaded} / ${formatBytes(progress.totalBytes)}`;
    }

    return `Transferring: ${loaded}`;
  }

  if (status === 'failed' && typeof error === 'string' && error.trim().length > 0) {
    return `Transfer failed: ${error}`;
  }

  switch (status) {
    case 'cloned':
      return 'Ready to decrypt and download';
    case 'pending':
      return 'Waiting to be transferred';
    case 'failed':
      return 'Transfer failed';
    case 'no-access':
      return 'No decryption access';
    default:
      return '';
  }
}

function getStatusClassName(status: FileCloneStatus): string {
  switch (status) {
    case 'cloned':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'cloning':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'pending':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'failed':
      return 'border-red-200 bg-red-50 text-red-700';
    case 'no-access':
      return 'border-slate-200 bg-slate-100 text-slate-600';
    default:
      return '';
  }
}

interface SixDetailCloneStatusIndicatorProps {
  file: FileEntry;
}

function SixDetailCloneStatusIndicator({
  file,
}: SixDetailCloneStatusIndicatorProps): React.ReactElement | null {
  const status = useFileCloneStatus(file);
  const error = useFileCloneError(file.cid);
  const progress = useCloningProgress(file.cid ?? '');

  if (status === 'unknown') return null;

  const label = getStatusLabel(status, progress, error);

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${getStatusClassName(status)}`}
      title={label}
    >
      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  );
}

export default SixDetailCloneStatusIndicator;
